import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { AppState } from 'src/app/root-store/state';
import { Observable } from 'rxjs';
import { WebsiteStatsModel } from '../shared/models/website-stats';
import { loadSearchFieldInitData } from 'src/app/root-store/announcement-store/front-page/search-field/search-field-actions';
import { selectSearchFieldStateData } from 'src/app/root-store/announcement-store/front-page/search-field/selectors';
import { Router } from '@angular/router';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {
  selectCategoriesTree,
  selectCustomerPortalUrl,
  selectHeadCategories,
  selectLocalities,
  selectPublishDateOptions,
  selectSalaryOptionsNew,
  selectSeniorityLevelOptions,
  selectTodayPublishDateOption,
} from 'src/app/root-store/selectors';
import { AnnouncementFilter } from 'src/app/shared/models/announcement-filter';
import { AnnouncementService } from 'src/app/shared/services/announcement.service';
import {
  AnnouncementTypeEnum,
  Locality,
  OccupationCategory,
  OptionItem,
  TreeSelectModel,
} from '@hrra/common-models';
import { DeviceDetectionService, EnvService, Range, SsrService, UI_LIMITS } from '@hrra/core';
import { SeniorityLevelItem } from 'src/app/shared/models/seniority-level.model';

@Component({
  selector: 'app-search-and-filter',
  templateUrl: `./redesign/search-and-filter.component.html`,
  styleUrls: [`./redesign/search-and-filter.component.scss`],
  standalone: false,
})
export class SearchAndFilterComponent implements OnInit {
  searchForm: UntypedFormGroup;
  initialData$: Observable<WebsiteStatsModel>;

  locations$: Observable<Locality[]>;
  todayPublishDateOption$: Observable<OptionItem<Range<number>>>;
  publishDateOptions$: Observable<OptionItem<Range<number>>[]>;
  headCategories$: Observable<OccupationCategory[]>;
  customerPortalUrl$: Observable<string>;
  public announcementTypes: any;

  salaryOptions$: Observable<OptionItem<Range<number>>[]>;
  seniorityLevelOptions$: Observable<SeniorityLevelItem[]>;

  providerWebsiteId: number = this.envService.tenantId;

  public breakPoint: any;

  public backgroundImageUrl = null;

  public maxSelectedValue: number = UI_LIMITS.MAX_VISIBLE_SELECTED_ITEMS;

  locations = new UntypedFormControl([]);
  categories = new UntypedFormControl([]);
  specializations = new UntypedFormControl([]);
  industries = new UntypedFormControl([]);
  seniorityLevels = new UntypedFormControl([]);
  experienceRange = new UntypedFormControl(null);
  salaryRangeOptionId = new UntypedFormControl(null);

  // the experience panel edits a draft range that only reaches the form on approve
  experienceFrom = new UntypedFormControl(null);
  experienceTo = new UntypedFormControl(null);
  experiencePanelVisible = false;

  @ViewChild('experienceChip')
  experienceChip: ElementRef<HTMLElement>;

  public categoryTree$: Observable<TreeSelectModel<number>[]>;
  public localityTree$: Observable<TreeSelectModel<number>[]>;

  onSearchButtonClick(
    salaryOptions: OptionItem<Range<number>>[],
    publishDateOptions: OptionItem<Range<number>>[]
  ) {
    let searchData = <AnnouncementFilter>{
      query: this.toValue(this.searchForm.get('searchKeyword').value),
      localityIds: this.toList(this.locations.value),
      categoryIds: this.toList(this.categories.value),
      specializationCodes: this.toList(this.specializations.value),
      industryCodes: this.toList(this.industries.value),
      seniorityLevelCodes: this.toList(this.seniorityLevels.value),
      salaryRangeOptionId: this.toValue(this.salaryRangeOptionId.value),
      experienceRange: this.toValue(this.experienceRange.value),
    };

    let q = this.announcementService.convertFilterToQueryParams(
      searchData,
      salaryOptions ?? [],
      publishDateOptions ?? []
    );

    this.router.navigate(['./search-posting'], {
      queryParams: q,
    });
  }

  onAnnouncementType(typeId: number) {
    let searchData = <AnnouncementFilter>{
      announcementTypeId: typeId,
    };

    let q =
      this.announcementService.convertQuickFilterToQueryParams(searchData);

    this.router.navigate(['./search-posting'], {
      queryParams: q,
    });
  }

  onTodayFilter(
    publishDateOption: OptionItem<Range<number>>,
    publishDateOptions: OptionItem<Range<number>>[]
  ) {
    let searchData = <AnnouncementFilter>{
      publishDateRangeOptionId: publishDateOption?.key,
    };

    let q = this.announcementService.convertPublishDateOptionsToQueryParams(
      searchData,
      publishDateOptions
    );

    this.router.navigate(['./search-posting'], {
      queryParams: q,
    });
  }

  toggleExperiencePanel() {
    if (!this.experiencePanelVisible) {
      let range: Range<number> = this.experienceRange.value;
      this.experienceFrom.setValue(range?.from ?? null);
      this.experienceTo.setValue(range?.to ?? null);
    }

    this.experiencePanelVisible = !this.experiencePanelVisible;
  }

  onExperienceReset() {
    this.experienceFrom.setValue(null);
    this.experienceTo.setValue(null);
    this.experienceRange.setValue(null);
    this.experiencePanelVisible = false;
  }

  onExperienceApprove() {
    let from = this.toYears(this.experienceFrom.value);
    let to = this.toYears(this.experienceTo.value);

    if (from !== null && to !== null && from > to) {
      [from, to] = [to, from];
    }

    this.experienceRange.setValue(from === null && to === null ? null : { from, to });
    this.experiencePanelVisible = false;
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent) {
    if (this.experiencePanelVisible && !this.experienceChip?.nativeElement.contains(<Node>event.target)) {
      this.experiencePanelVisible = false;
    }
  }

  private toYears(value: any): number {
    let years = Number(value);
    return value === null || value === undefined || value === '' || isNaN(years)
      ? null
      : Math.max(0, Math.floor(years));
  }

  //the query param converters skip a filter only when it is undefined, so empty values are normalized here
  private toList<T>(value: T[]): T[] {
    return value !== undefined && value !== null && value.length > 0
      ? value
      : undefined;
  }

  private toValue<T>(value: T): T {
    return value !== undefined && value !== null && <any>value !== ''
      ? value
      : undefined;
  }

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private announcementService: AnnouncementService,
    private envService: EnvService,
    private deviceDetectionService: DeviceDetectionService,
    private ssrService: SsrService
  ) {
    this.announcementTypes = AnnouncementTypeEnum;
  }

  ngOnInit() {
    this.searchForm = new UntypedFormGroup({
      searchKeyword: new UntypedFormControl(null),
      locations: this.locations,
      categories: this.categories,
      specializations: this.specializations,
      industries: this.industries,
      seniorityLevels: this.seniorityLevels,
      experienceRange: this.experienceRange,
      salaryRangeOptionId: this.salaryRangeOptionId,
    });

    this.store.dispatch(loadSearchFieldInitData());

    this.initialData$ = this.store.pipe(select(selectSearchFieldStateData));
    this.locations$ = this.store.pipe(select(selectLocalities));
    this.todayPublishDateOption$ = this.store.pipe(
      select(selectTodayPublishDateOption)
    );
    this.publishDateOptions$ = this.store.pipe(
      select(selectPublishDateOptions)
    );
    this.headCategories$ = this.store.pipe(select(selectHeadCategories));
    this.customerPortalUrl$ = this.store.pipe(select(selectCustomerPortalUrl));

    this.salaryOptions$ = this.store.pipe(select(selectSalaryOptionsNew));
    this.seniorityLevelOptions$ = this.store.pipe(
      select(selectSeniorityLevelOptions)
    );

    this.categoryTree$ = this.store.pipe(select(selectCategoriesTree));
    // this.localityTree$ = this.store.pipe(select(selectLocalitiesTree))

    this.setBackgroundByWidth();

    if (this.ssrService.isPlatformBrowser()) {
      window.addEventListener('resize', () => {
        this.setBackgroundByWidth();
      });
    }
  }

  setBackgroundByWidth() {
    const deviceInfo = this.deviceDetectionService.detectDevice();
    if (deviceInfo.isDesktop) {
      this.backgroundImageUrl = `url("/assets/img/search-main-page/search-field-image-desktop-${this.providerWebsiteId}.jpg")`;
    } else if (deviceInfo.isTablet) {
      this.backgroundImageUrl = `url("/assets/img/search-main-page/search-field-image-tab-large-${this.providerWebsiteId}.jpg")`;
    } else if (deviceInfo.isMobile) {
      //this.backgroundImageUrl = `url("/assets/img/search-main-page/search-field-image-tab-small-${this.providerWebsiteId}.jpg")`;
      this.backgroundImageUrl = `url("/assets/img/search-main-page/search-field-image-mobile-${this.providerWebsiteId}.jpg")`;
    } else {
      this.backgroundImageUrl = `url("/assets/img/search-main-page/search-field-image-mobile-${this.providerWebsiteId}.jpg")`;
    }
  }
}
